/**
 * Format a value for display, handling arrays, objects, and null values
 * @param {*} value - The value to format
 * @returns {string} - Formatted value for display
 */
export const formatValue = (value) => {
  if (value === null || value === undefined) {
    return 'N/A';
  }

  // Don't format location data as we'll use a map component
  if (typeof value === 'string' && (
    value.includes('"lat":') || 
    value.includes('"lng":') || 
    value.includes('passed_location')
  )) {
    try {
      // Try to parse as JSON to see if it's location data
      const parsed = JSON.parse(value);
      if ((parsed.lat && parsed.lng) || (parsed.passed_location)) {
        return '[Location data shown on map]';
      }
    } catch (e) {
      // Not JSON, continue with normal formatting
    }
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return 'N/A';
    
    // Check if array contains objects
    if (typeof value[0] === 'object' && value[0] !== null) {
      return value.map(item => formatObject(item)).join(', ');
    }
    
    return value.join(', ');
  }

  if (typeof value === 'object') {
    return formatObject(value);
  }

  return String(value);
};

/**
 * Format an object for display
 * @param {Object} obj - The object to format
 * @returns {string} - Formatted object as string
 */
export const formatObject = (obj) => {
  if (!obj) return 'N/A';
  
  try {
    // Handle special cases for address objects
    if (obj.address1 || obj.city || obj.state || obj.zip) {
      const addressParts = [];
      if (obj.address1) addressParts.push(obj.address1);
      if (obj.address2) addressParts.push(obj.address2);
      if (obj.city) addressParts.push(obj.city);
      if (obj.state) addressParts.push(obj.state);
      if (obj.zip) addressParts.push(obj.zip);
      if (obj.country) addressParts.push(obj.country);
      
      return addressParts.join(', ');
    }
    
    // For bank details
    if (obj.account || obj.ifsc) {
      const bankParts = [];
      if (obj.name) bankParts.push(`Bank: ${obj.name}`);
      if (obj.account) bankParts.push(`Acc: ${obj.account}`);
      if (obj.ifsc) bankParts.push(`IFSC: ${obj.ifsc}`);
      
      return bankParts.join(', ');
    }
    
    // Default object formatting
    return Object.entries(obj)
      .filter(([_, v]) => v !== null && v !== undefined)
      .map(([k, v]) => `${k}: ${typeof v === 'object' ? formatValue(v) : v}`)
      .join(', ');
  } catch (error) {
    console.error('Error formatting object:', error);
    return JSON.stringify(obj);
  }
};

/**
 * Get a color based on the source of the data
 * @param {string} source - The data source
 * @returns {string} - Hex color code
 */
export const getSourceColor = (source) => {
  const sourceColors = {
    'abfrl': '#FF6B6B',
    'bigbasket': '#4ECDC4',
    'boat': '#1A535C',
    'canva': '#FF9F1C',
    'dominos': '#E71D36',
    'dubsmash': '#2EC4B6',
    'dunzo': '#FFBF69',
    'expressbees': '#CBF3F0',
    'hathway': '#FFCBF2',
    'icmr': '#F15BB5',
    'moneycontrol': '#9B5DE5',
    'questapp': '#00BBF9',
    'railyatri': '#00F5D4',
    'rentmojo': '#F15BB5',
    'shein': '#FEE440',
    'truecaller': '#00BBF9',
    'upstox': '#F29E4C',
    'vedantu': '#0AAEB8',
    'zomato': '#FF5C5C',
    'zoomcar': '#00A878',
  };
  
  return sourceColors[source?.toLowerCase()] || '#6C63FF';
};

/**
 * Get icon name based on field type
 * @param {string} fieldName - The field name
 * @returns {string} - Material icon name
 */
export const getFieldIcon = (fieldName) => {
  const iconMap = {
    // Personal information
    'first_name': 'person',
    'last_name': 'person',
    'gender': 'wc',
    'dob': 'cake',
    'father_or_spouse_name': 'family_restroom',
    'username': 'account_circle',
    
    // Contact information
    'email': 'email',
    'mobile_number': 'phone',
    'phone_make': 'smartphone',
    
    // Address information
    'addresses': 'home',
    'city': 'location_city',
    'state': 'map',
    'pincode': 'pin_drop',
    'area': 'place',
    'lat_long': 'my_location',
    'first_known_location': 'place',
    'last_known_location': 'place',
    
    // Financial information
    'bank': 'account_balance',
    'aadhaar': 'badge',
    'pan': 'credit_card',
    
    // Account information
    'password': 'password',
    'source': 'source',
    
    // Social information
    'facebook': 'facebook',
    'twitter': 'alternate_email',
    
    // Other icons
    'job': 'work'
  };
  
  // Make sure we return a valid icon name that exists in Material Icons
  return iconMap[fieldName] || 'info';
};

/**
 * Group data fields by category for better display
 * @param {Object} data - The user data
 * @returns {Object} - Grouped data by category
 */
export const groupDataByCategory = (data) => {
  const groups = {
    'Personal Information': ['first_name', 'last_name', 'gender', 'dob', 'father_or_spouse_name', 'username'],
    'Contact Information': ['email', 'mobile_number', 'phone_make'],
    'Address Information': ['addresses', 'city', 'state', 'pincode', 'lat_long', 'area'],
    'Financial Information': ['bank', 'pan', 'aadhaar'],
    'Account Information': ['password', 'source'],
    'Other Information': []
  };
  
  const result = {};
  const processedFields = new Set();
  
  // First, assign fields to their specific groups
  Object.entries(groups).forEach(([groupName, fields]) => {
    result[groupName] = {};
    
    fields.forEach(field => {
      if (data[field] !== undefined) {
        result[groupName][field] = data[field];
        processedFields.add(field);
      }
    });
    
    // Remove empty groups
    if (Object.keys(result[groupName]).length === 0) {
      delete result[groupName];
    }
  });
  
  // Then, add any remaining fields to "Other Information"
  Object.entries(data).forEach(([field, value]) => {
    if (!processedFields.has(field) && field !== '_id' && field !== '__v') {
      if (!result['Other Information']) {
        result['Other Information'] = {};
      }
      result['Other Information'][field] = value;
    }
  });
  
  return result;
};

/**
 * Extract searchable fields from data
 * @param {Object} data - The user data
 * @returns {Object} - Object with searchable fields
 */
export const extractSearchableFields = (data) => {
  const searchableFields = [
    'email', 
    'mobile_number', 
    'first_name', 
    'last_name', 
    'dob', 
    'source', 
    'city', 
    'aadhaar', 
    'pan'
  ];
  
  const result = {};
  
  searchableFields.forEach(field => {
    if (data[field]) {
      result[field] = data[field];
    }
  });
  
  return result;
}; 